import { STATUS_META, TOPIC_PALETTE } from "../constants";

function normalizeHex(color: string): string {
  return color.trim().toLowerCase();
}

/** Status badge bg / fg / dot — topics must never reuse these. */
export function reservedStatusColors(): Set<string> {
  const reserved = new Set<string>();
  for (const meta of Object.values(STATUS_META)) {
    reserved.add(normalizeHex(meta.bg));
    reserved.add(normalizeHex(meta.fg));
    reserved.add(normalizeHex(meta.dot));
  }
  return reserved;
}

/**
 * Pick a topic fg color that is:
 * 1. Not already used by another topic (when possible)
 * 2. Never equal to a status-badge color
 * Falls back to the least-used safe palette entry when the palette is exhausted.
 */
export function pickTopicColor(usedFgColors: string[] = []): string {
  const reserved = reservedStatusColors();
  const used = usedFgColors.map(normalizeHex).filter(Boolean);

  const safePalette = TOPIC_PALETTE.filter(
    (p) =>
      !reserved.has(normalizeHex(p.fg)) && !reserved.has(normalizeHex(p.bg)),
  );

  const unused = safePalette.filter(
    (p) => !used.includes(normalizeHex(p.fg)),
  );
  if (unused.length > 0) {
    return unused[0].fg;
  }

  // Palette exhausted — reuse the least-used safe color.
  let best = safePalette[0] || TOPIC_PALETTE[0];
  let bestCount = Number.POSITIVE_INFINITY;
  for (const entry of safePalette) {
    const key = normalizeHex(entry.fg);
    const count = used.filter((c) => c === key).length;
    if (count < bestCount) {
      bestCount = count;
      best = entry;
    }
  }
  return best.fg;
}
